import { useState, useEffect, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { VStack, StackDivider, Text, HStack, Alert, AlertIcon } from "@chakra-ui/react";
import { useCurrencyFormatter } from "components/FormattedMoney/useCurrencyFormatter";
import { useSessionCtx, useSessionJWT } from "utils";
import { getPayoutCalculations } from "api/invoicing";
import { FullPageSpinner } from "components/Spinner";

const parseValues = (values) => {
  if (typeof values !== "object") return null;
  const amount = values.amount;
  const paymentMethodId = values.destinationPaymentMethodId;
  const currency = values.currency || "USD"; // if this is non-USD (i.e. virtual currencies), calc-fees conversions to USD will be based on the source currency

  let isValidPayload = Number.isInteger(amount) && paymentMethodId && currency;

  if (isValidPayload) {
    return { amount, paymentMethodId, currency };
  } else {
    return null;
  }
};

const NonBankPayoutReview = ({ values, onPayoutCalculationsLoaded }) => {
  const { formatMessage } = useIntl();
  const { sessionJWT } = useSessionJWT();
  const { sessionId } = useSessionCtx();
  const [isLoadingPayoutCalc, setIsLoadingPayoutCalc] = useState(false);
  const [getPayoutCalcError, setGetPayoutCalcError] = useState(null);
  const [payoutCalculations, setPayoutCalculations] = useState(null);
  const processingCurrency = values?.payoutDestination?.processing_currency || "USD"
  const formatter = useCurrencyFormatter(processingCurrency);
  const fromCurrencyFormatter = useCurrencyFormatter(values?.currency || "USD"); // since payout will always be in USD, this is to handle if the original currency was not USD (as in, virtual currencies)

  const loadPayoutCalculations = useCallback(async () => {
    setIsLoadingPayoutCalc(true);
    setGetPayoutCalcError(null);
    setPayoutCalculations(setPayoutCalculations);
    try {
      const ctx = {
        ...sessionJWT,
        sessionId,
      };
      const body = parseValues(values);
      if (!body) {
        throw new Error(formatMessage({
          defaultMessage: "Missing required data.",
          description: "Alert title"
        }));
      }
      const data = await getPayoutCalculations(ctx, body);
      const pmMatch = data.payload.payment_methods.find(
        (pm) => pm.id === values.destinationPaymentMethodId,
      );
      if (pmMatch) {
        const parsedData = {
            provider: pmMatch.provider,
            payout_requested: pmMatch.options[0].payout_total, // currently we expect there to always be 1 entry in options here
            payout_fee: pmMatch.options[0].payout_fee,
            payout_total: pmMatch.options[0].payout_total_minus_fees,
        };
        setPayoutCalculations(parsedData);
        onPayoutCalculationsLoaded && onPayoutCalculationsLoaded(parsedData);
      } else {
        throw new Error("There was an error retrieving payout calculations for the selected option.");
      }
    } catch (err) {
      setGetPayoutCalcError(err);
    }
    setIsLoadingPayoutCalc(false);
  }, [values, sessionId, sessionJWT, formatMessage, onPayoutCalculationsLoaded]);

  useEffect(() => {
    if (!payoutCalculations) loadPayoutCalculations(); // formik values being set can trigger this multiple times, only run if we're missing calcs
  }, [loadPayoutCalculations, payoutCalculations]);

  if (isLoadingPayoutCalc) {
    return <FullPageSpinner />;
  } else if (getPayoutCalcError) {
    return (
      <Alert status="error">
        <AlertIcon />
        {getPayoutCalcError?.message || (
          <FormattedMessage defaultMessage="There was an error." description="Alert title" />
        )}
      </Alert>
    );
  } else if (payoutCalculations) {
    const { provider, payout_requested, payout_fee, payout_total } = payoutCalculations;
    return (
      <VStack
        data-testid="non-bank-payout-confirm-and-review"
        width="100%"
        align="flex-start"
        p="4"
        borderWidth="1px"
        borderRadius="4px"
        gap="2"
        bgColor={"background"}
        boxShadow="base"
        divider={<StackDivider />}
      >
        <HStack justify="space-between" width="100%">
          <Text fontSize="sm" color="secondaryText">
            <FormattedMessage
              defaultMessage="Destination:"
              description="label for where transfered money will go to"
            />
          </Text>
          <VStack align="flex-end" spacing="0">
            <Text size="sm">{provider.toUpperCase()}</Text>
          </VStack>
        </HStack>
        <HStack justify="space-between" width="100%">
          <Text fontSize="sm" color="secondaryText">
            <FormattedMessage
              defaultMessage="Requested amount:"
              description="label for amount requested in payout"
            />
          </Text>
          <HStack>
            <Text size="sm">{formatter(payout_requested)}</Text>
            {(values.currency !== processingCurrency) && values.amount && (
              <Text as="i" fontSize="sm" color="secondaryText">&nbsp;({fromCurrencyFormatter(values.amount)})</Text>
            )}
          </HStack>
        </HStack>
        <HStack justify="space-between" width="100%">
          <Text fontSize="sm" color="secondaryText">
            <FormattedMessage defaultMessage="Fee:" description="label for fee amount with payout" />
          </Text>
          <Text size="sm">{formatter(payout_fee)}</Text>
        </HStack>
        <HStack justify="space-between" width="100%">
          <Text fontWeight="700" fontSize="sm" color="secondaryText">
            <FormattedMessage
              defaultMessage="Payout total:"
              description="label for payout amount including fees"
            />
          </Text>
          <Text fontWeight="700" size="sm">
            {formatter(payout_total)}
          </Text>
        </HStack>
      </VStack>
    );
  }

  return null; // shouldn't get here...
};

export default NonBankPayoutReview;
